import { type RefObject, useEffect, useRef } from 'react';

import type { MapRef } from 'react-map-gl/mapbox';

import stationMarkerUrl from '@/assets/images/station-marker.png?url';
import { STATION_MARKER } from '@/utils/constants/map';
import loadRasterImage from '@/utils/helpers/load-raster-image';

const IMAGE_URLS: Record<string, { url: string; sdf?: boolean }> = {
  [STATION_MARKER]: { url: stationMarkerUrl },
};

const useMapImageLoader = (mapRef: RefObject<MapRef | null>, isMapLoaded: boolean) => {
  const loadingImagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isMapLoaded || !mapRef?.current) {
      return;
    }

    const map = mapRef.current.getMap();

    const handleImageMissing = async (e: { id: string }) => {
      const id = e.id;
      const config = IMAGE_URLS[id];

      if (!config) {
        return;
      }

      if (map.hasImage(id)) {
        return;
      }

      if (loadingImagesRef.current.has(id)) {
        return;
      }

      loadingImagesRef.current.add(id);

      try {
        await loadRasterImage(map, id, config.url, { sdf: config.sdf });
      } catch (err) {
        console.error(`Failed to load image: ${id}`, err);
        loadingImagesRef.current.delete(id);
      }
    };

    map.on('styleimagemissing', handleImageMissing);

    return () => {
      map.off('styleimagemissing', handleImageMissing);
    };
  }, [mapRef, isMapLoaded]);
};

export default useMapImageLoader;
